import { useEffect, useMemo, useRef, useState, type ReactNode, type Ref, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, dayLabel, hhmm, initials, plural, rubles } from '@limuzin/kit';
import type { PassengerRideView } from '@limuzin/passenger';
import { cancelRide, clearHistory, hideRide } from '../api';
import { isCancelled, useAvatar, usePreorders, useHistory, useSession } from '../session';
import { Icon, type IconName } from '../material';
import { ActionIcon } from '../icons';
import {
  Button, ConfirmSheet, EmptyCard, IconButton, Initials, RatingStar, RouteDetail, RouteEnds,
  RouteSummary, Soft, TransferNumber,
  pickupOf, destinationOf, useFits,
} from '../widgets';
import { repeat, useDraft } from './draft';
import { CancelSheet, cancelNotSent, statusLabel } from './ride';

type Tab = 'scheduled' | 'history';
type Filter = 'all' | 'done' | 'cancelled';

/**
 * «Мои поездки» — the preorders and everything that is over. The ride under way is
 * not here: «Заказ» is the one place it is shown. A port of `orders_screen.dart`.
 */
export function OrdersScreen() {
  // «Запланированные» whenever «Поездки» is opened from the bar: what is coming is
  // what the tab is for, and «История» is a look back the passenger asks for.
  const [tab, setTab] = useState<Tab>('scheduled');
  // A предзаказ just placed, sent here by «Новый заказ» to be brought into view.
  const [focus, setFocus] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sent = (location.state as { focus?: string } | null)?.focus ?? null;

  // Every arrival at «Поездки» — from the bar, from the other tabs, back from a chat —
  // is a new location, the bar's link to the page already open included.
  const arrived = location.pathname === '/orders' ? location.key : null;
  useEffect(() => {
    if (arrived) setTab('scheduled');
  }, [arrived]);

  useEffect(() => {
    if (!sent) return;
    setTab('scheduled');
    setFocus(sent);
    // Once taken, so that going back to this entry does not do it again.
    navigate(location.pathname, { replace: true, state: null });
  }, [sent, location.pathname, navigate]);

  return (
    <div className="screen">
      <div className="screen-head column">
        <h1 className="title">Мои поездки</h1>
        <p className="subtitle">Предзаказы и история</p>
      </div>
      <div className="orders-tabs">
        <div className="column orders-tabs-row">
          {(
            [
              ['scheduled', 'Запланированные'],
              ['history', 'История'],
            ] as [Tab, string][]
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={tab === value ? 'on' : ''}
              onClick={() => {
                setTab(value);
                setFocus(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="screen-scroll">
        <div className="column">
          {tab === 'scheduled' ? (
            <ScheduledTab focus={focus} onFocused={() => setFocus(null)} />
          ) : (
            <HistoryTab />
          )}
        </div>
      </div>
    </div>
  );
}

/** A предзаказ that has set off, as opposed to one that is still an arrangement. */
const departed = (ride: PassengerRideView) =>
  ['EN_ROUTE', 'ON_SITE', 'IN_PROGRESS'].includes(ride.status);

function ScheduledTab({ focus, onFocused }: { focus: string | null; onFocused: () => void }) {
  const { api, reload } = useSession();
  const preorders = usePreorders();
  // The one card open, as in «История» and the driver's «Заказы».
  const [openId, setOpenId] = useState<string | null>(null);
  const focused = useRef<HTMLDivElement | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // The ride as the list has it now, so the question follows it if it sets off
  // while the sheet is open — and the sheet goes if the ride leaves the list.
  const asked = preorders.find((ride) => ride.id === asking);
  const present = focus !== null && preorders.some((ride) => ride.id === focus);

  // The card just placed arrives open, and in view.
  useEffect(() => {
    if (!focus) return;
    onFocused();
    if (present) setOpenId(focus);
  }, [focus, present, onFocused]);

  useEffect(() => {
    if (openId) focused.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [openId]);

  const cancel = async (id: string) => {
    setCancelling(id);
    setError(null);
    try {
      await cancelRide(api, id);
      await reload();
      setAsking(null);
    } catch (problem) {
      const ride = preorders.find((r) => r.id === id);
      if (problem instanceof ApiError && problem.code === 'offline' && ride) {
        // Said along the bottom with the number to ring, and the answer may be what
        // was lost rather than the cancel.
        setAsking(null);
        cancelNotSent(ride);
        void reload();
        return;
      }
      setError(problem instanceof ApiError ? problem : new ApiError('unknown', 'Что-то пошло не так'));
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="orders-list" style={{ paddingTop: 22 }}>
      {preorders.length === 0 ? (
        <EmptyCard
          title="Нет запланированных поездок"
          hint="Выберите «Сегодня» или «Завтра» в новом заказе, чтобы подать машину к нужному времени"
        />
      ) : (
        preorders.map((ride) => (
          <PreorderCard
            key={ride.id}
            ride={ride}
            open={ride.id === openId}
            unread={ride.unread > 0}
            cardRef={ride.id === openId ? focused : undefined}
            onToggle={() => setOpenId((id) => (id === ride.id ? null : ride.id))}
          >
            <PreorderActions
              cancelling={cancelling === ride.id}
              disabled={cancelling !== null}
              onCancel={() => {
                setError(null);
                setAsking(ride.id);
              }}
              phone={ride.driver?.phone ?? null}
              chat={ride.driver ? `/orders/chat/${encodeURIComponent(ride.id)}` : null}
              unread={ride.unread > 0}
            />
          </PreorderCard>
        ))
      )}
      {asked && (
        <CancelSheet
          ride={asked}
          busy={cancelling === asked.id}
          error={error}
          onConfirm={() => void cancel(asked.id)}
          onClose={() => setAsking(null)}
        />
      )}
    </div>
  );
}

/**
 * A предзаказ in «Запланированные», drawn as the driver's card for the same order in
 * their «Предварительные» is. Folded, it is «ЗАКАЗ № 1042», the day and the time in a
 * badge, and every point of the route. Opened, the route at its head gives way to where
 * the order stands — where the driver's card puts the passenger — and under a rule come
 * the driver once one is booked, drawn as the driver's card draws its passenger, the
 * route labelled, the comment, and the buttons ([children]). What the driver drives is
 * left to the ride itself. A port of `_PreorderCard`.
 */
function PreorderCard({
  ride,
  open,
  cardRef,
  onToggle,
  unread,
  children,
}: {
  ride: PassengerRideView;
  open: boolean;
  /** The driver wrote and it is not read: a dot by the number folded, on «Чат» open. */
  unread: boolean;
  cardRef?: Ref<HTMLDivElement>;
  onToggle: () => void;
  children: ReactNode;
}) {
  const at = new Date(ride.scheduledFor!);
  const comment = ride.comment?.trim() ?? '';
  return (
    <div ref={cardRef} className={open ? 'preorder-card open' : 'preorder-card'}>
      <button type="button" className="preorder-hit" onClick={onToggle} aria-expanded={open}>
        <span className="row preorder-head">
          <OrderNumber number={ride.number} dot={unread && !open} />
          <span className="time-badge">
            <span className="label">{dayLabel(at)},</span>
            <b>{hhmm(at)}</b>
          </span>
        </span>
        <span className="preorder-swap" key={open ? 'status' : 'route'}>
          {open ? (
            <PreorderStatus ride={ride} />
          ) : (
            <>
              <RouteSummary stops={ride.stops} />
              <StatusLine ride={ride} />
            </>
          )}
        </span>
      </button>
      {open && (
        <div className="preorder-open">
          <div onClick={onToggle} style={{ cursor: 'pointer' }}>
            {ride.driver && <BookedDriver driver={ride.driver} />}
            <RouteDetail stops={ride.stops} />
          </div>
          {comment && (
            <div className="comment-box">
              <span className="icon">
                <ActionIcon glyph="comment" size={20} />
              </span>
              <span>{comment}</span>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Who is booked for a предзаказ, as the driver's open card puts its passenger: the face
 * or the initials, the name, and under it the rating after a drawn star. A port of
 * `_BookedDriver`.
 */
function BookedDriver({ driver }: { driver: NonNullable<PassengerRideView['driver']> }) {
  const face = useAvatar(driver.avatarKey);
  return (
    <div className="row preorder-driver">
      <Initials text={initials(driver.name)} size={40} src={face} />
      <div className="grow">
        <div className="preorder-driver-name">{driver.name || 'Водитель'}</div>
        <div className="row preorder-driver-rating">
          <RatingStar size={13} color="var(--destination)" />
          {driver.rating.toFixed(1)}
        </div>
      </div>
    </div>
  );
}

/**
 * Where a предзаказ stands, in the passenger's words, with its glyph and the tone it is
 * drawn in — said once, for the open card's head and the folded card's line alike.
 *
 * «Ожидает водителя» covers both the hours when drivers can take it from their
 * «Предварительные» and the last minutes when dispatch offers it round: to the
 * passenger the two are the same fact. One whose driver gave it up says so instead, in
 * the red of a thing undone, until somebody else takes it. One whose driver has set off,
 * which this app keeps here to the end (ADR 0017), says the state the car is in. A port
 * of `_statusOf`.
 */
function statusOf(ride: PassengerRideView): {
  label: string;
  hint: string | null;
  icon: IconName;
  tone: string;
} {
  if (departed(ride)) {
    return { label: statusLabel(ride), hint: null, icon: 'local_taxi_rounded', tone: 'under-way' };
  }
  if (ride.driver) {
    return {
      label: 'Водитель назначен',
      hint: 'Машина приедет к назначенному времени',
      icon: 'check_rounded',
      tone: 'assigned',
    };
  }
  if (ride.refusedAt) {
    return {
      label: 'Водитель отказался — ищем другого',
      hint: 'Заказ снова виден водителям',
      icon: 'undo_rounded',
      tone: 'refused',
    };
  }
  return {
    label: 'Ожидает водителя',
    hint: 'Водители уже видят заказ',
    icon: 'schedule_rounded',
    tone: 'waiting',
  };
}

/**
 * The status at the head of an open card, where the driver's card puts the passenger:
 * a disc in its colours where the avatar would be, the status where the name would be,
 * and under it what it means.
 */
function PreorderStatus({ ride }: { ride: PassengerRideView }) {
  const { label, hint, icon, tone } = statusOf(ride);
  return (
    <span className={`row preorder-status ${tone}`}>
      <span className="preorder-status-disc">
        <Icon name={icon} size={20} />
      </span>
      <span className="grow">
        <span className="preorder-status-label" style={{ display: 'block' }}>
          {label}
        </span>
        {hint && (
          <span className="preorder-status-hint" style={{ display: 'block' }}>
            {hint}
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * The status on a folded card, under its route: the open card's glyph and words, small,
 * on the status's own wash — so a card that has changed under the passenger says so
 * without being opened. A port of `_StatusLine`.
 */
function StatusLine({ ride }: { ride: PassengerRideView }) {
  const { label, icon, tone } = statusOf(ride);
  return (
    <span className={`status-line ${tone}`}>
      <Icon name={icon} size={15} />
      {label}
    </span>
  );
}

/**
 * «ЗАКАЗ № 1042», as the driver's order cards write it, and the dot beside it when the
 * driver wrote. Where the line is too narrow for all of it, «ЗАКАЗ» comes off and «№ 1042» stands alone — the figure is what поддержка
 * will ask for. A port of `_OrderNumber`.
 */
function OrderNumber({ number, dot }: { number: number; dot: boolean }) {
  const [space, whole, fits] = useFits();
  // The driver wrote and it is not read: a dot beside the figure, on the folded card.
  const figure = (
    <>
      <b>{number}</b>
      {dot && <span className="unread-dot inline pulse" aria-label="Есть непрочитанные сообщения" />}
    </>
  );
  return (
    <span className="grow order-number-room" ref={space as RefObject<HTMLSpanElement>}>
      <span className="row order-number">
        <span className="label" style={{ color: 'var(--ink)' }}>
          {fits ? 'Заказ №' : '№'}
        </span>
        {figure}
      </span>
      {/* The whole of it, unseen, for what the line would need to hold it. */}
      <span className="row order-number measure" aria-hidden="true" ref={whole as RefObject<HTMLSpanElement>}>
        <span className="label">Заказ №</span>
        {figure}
      </span>
    </span>
  );
}

/**
 * «Чат» and «Звонок» together against the left edge once a driver is booked, and
 * «Отменить» held apart against the right, as the driver's card holds «Отказ» apart from
 * «Чат» and «Звонок»:
 * their pills, 44 high and round-ended, with their glyphs. Where the words do not fit
 * the glyphs keep the arrangement without them. A port of `_PreorderActions`.
 */
function PreorderActions({
  cancelling,
  disabled,
  onCancel,
  phone,
  chat,
  unread,
}: {
  cancelling: boolean;
  disabled: boolean;
  onCancel: () => void;
  /** Both null until a driver is booked: there is nobody to ring or write to. */
  phone: string | null;
  /** Where the ride's chat is — over «Поездки», so the way back is to this card. */
  chat: string | null;
  /** The driver wrote and it is not read yet: the dot on «Чат». */
  unread: boolean;
}) {
  const [space, whole, fits] = useFits();
  const cancel = cancelling ? 'Отменяем…' : 'Отменить';
  const pills = (labelled: boolean) => (
    <>
      {chat && (
        <Link
          className={labelled ? 'card-action chat' : 'card-action chat compact'}
          to={chat}
          aria-label={unread ? 'Чат, есть непрочитанные' : 'Чат'}
          title={labelled ? undefined : 'Чат'}
          tabIndex={labelled ? undefined : -1}
        >
          <ActionIcon glyph="chat" size={20} />
          {labelled && <span>Чат</span>}
          {unread && <span className="unread-dot ringed pulse" />}
        </Link>
      )}
      {/* `tel:` opens the dialler on a phone and the calling app on a desktop, which
          is the best a browser can do and enough. */}
      {phone && (
        <a
          className={labelled ? 'card-action call' : 'card-action call compact'}
          href={`tel:${phone}`}
          aria-label="Звонок"
          title={labelled ? undefined : 'Звонок'}
          tabIndex={labelled ? undefined : -1}
        >
          <ActionIcon glyph="call" size={19} />
          {labelled && <span>Звонок</span>}
        </a>
      )}
      <span className="grow" />
      <button
        type="button"
        className={labelled ? 'card-action refuse' : 'card-action refuse compact'}
        disabled={disabled}
        onClick={onCancel}
        aria-label={cancel}
        title={labelled ? undefined : cancel}
      >
        <ActionIcon glyph="refuse" size={15} />
        {labelled && <span>{cancel}</span>}
      </button>
    </>
  );
  return (
    <div className="preorder-actions-room" ref={space as RefObject<HTMLDivElement>}>
      <div className="row preorder-actions">{pills(fits)}</div>
      {/* The labelled row, unseen, for what it would need. */}
      <div
        className="row preorder-actions measure"
        aria-hidden="true"
        inert
        ref={whole as RefObject<HTMLDivElement>}
      >
        {pills(true)}
      </div>
    </div>
  );
}

function HistoryTab() {
  const history = useHistory();
  const { api, loading, reload } = useSession();
  const { set } = useDraft();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [day, setDay] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [openId, setOpenId] = useState<string | null>(null);
  // What the sheet is asking about: one ride, or the whole of «История».
  const [removing, setRemoving] = useState<PassengerRideView | 'all' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const days = useMemo(
    () => new Set(history.map((ride) => dayKey(new Date(ride.createdAt)))),
    [history],
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return history.filter((ride) => {
      if (filter === 'done' && ride.status !== 'COMPLETED') return false;
      if (filter === 'cancelled' && !isCancelled(ride)) return false;
      if (day && dayKey(new Date(ride.createdAt)) !== day) return false;
      if (!needle) return true;
      const hay = `${pickupOf(ride.stops)} ${destinationOf(ride.stops)} ${ride.driver?.name ?? ''}`;
      return hay.toLowerCase().includes(needle);
    });
  }, [history, query, filter, day]);

  const total = visible
    .filter((ride) => ride.status === 'COMPLETED')
    // The same figure each card shows, or the line above them adds up to something
    // other than the cards under it.
    .reduce((sum, ride) => sum + (ride.payment?.totalKopeks ?? ride.fare.totalKopeks), 0);

  // Nothing to search and nothing to filter: the passenger is told what will be here,
  // and given the way to put something here. Not while the rides are still on their
  // way — an empty history then is only one that has not arrived.
  if (history.length === 0 && !loading) {
    return (
      <div className="orders-list" style={{ paddingTop: 22 }}>
        <EmptyCard
          title="Поездок пока нет"
          hint="Здесь будут все ваши поездки: маршрут, время и стоимость. Любую можно будет повторить в одно касание."
        />
        <div style={{ marginTop: 4 }}>
          <Button onClick={() => navigate('/')}>Заказать поездку</Button>
        </div>
      </div>
    );
  }

  const reset = () => {
    setQuery('');
    setFilter('all');
    setDay(null);
    setCalendarOpen(false);
  };

  // Only over the whole of it: with a search, a chip or a day on, «Очистить» would
  // take rides the passenger cannot see.
  const whole = !query.trim() && filter === 'all' && !day;

  const ask = (what: PassengerRideView | 'all') => {
    setError(null);
    setRemoving(what);
  };

  /**
   * «Удалить из истории» or «Очистить историю», once asked: sent, and the list asked
   * for again. The rides are only hidden from the passenger — the panel keeps them
   * (ADR 0057) — and it needs a connection (ADR 0030): without one the sheet says so,
   * and the passenger tries again.
   */
  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    setError(null);
    try {
      await (removing === 'all' ? clearHistory(api) : hideRide(api, removing.id));
      await reload();
      setRemoving(null);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem : new ApiError('unknown', 'Что-то пошло не так'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '16px 20px 20px' }}>
      <div className="row" style={{ gap: 0 }}>
        <label className="search grow">
          <Icon name="search" size={17} style={{ color: 'var(--faint)' }} />
          <input
            className="grow"
            placeholder="Поиск по адресу"
            value={query}
            enterKeyHint="search"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <IconButton
          icon="calendar_today_outlined"
          size={19}
          label="Выбрать дату"
          className="big"
          style={{ color: calendarOpen || day ? 'var(--accent)' : 'var(--faint)' }}
          onClick={() => setCalendarOpen((open) => !open)}
        />
      </div>

      {calendarOpen && (
        <Calendar
          month={month}
          selected={day}
          days={days}
          onMonth={(delta) => setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1))}
          onPick={(value) => {
            setDay(value);
            setCalendarOpen(false);
          }}
        />
      )}

      {/* Each chip as wide as its own words; the row slides when the three outgrow it. */}
      <div className="filter-chips">
        {(
          [
            ['all', 'Все'],
            ['done', 'Успешные'],
            ['cancelled', 'Отменённые'],
          ] as [Filter, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={filter === value ? 'filter-chip on' : 'filter-chip'}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="row orders-count">
        <span className="grow">
          {visible.length} {plural(visible.length, 'поездка', 'поездки', 'поездок')}
        </span>
        <span>{rubles(total)}</span>
      </div>

      {visible.length === 0 ? (
        // A search or a filter left nothing. The way back out sits under the words —
        // a day picked in the calendar is otherwise undone only from inside the
        // calendar, which is shut by now.
        <div style={{ textAlign: 'center', padding: '28px 0' }}>
          <p className="body" style={{ margin: '0 0 12px' }}>
            Ничего не найдено
          </p>
          <Soft onClick={reset}>Сбросить фильтры</Soft>
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {visible.map((ride) => (
            <HistoryCard
              key={ride.id}
              ride={ride}
              open={openId === ride.id}
              onToggle={() => setOpenId(openId === ride.id ? null : ride.id)}
              onRepeat={() => {
                set(repeat(ride.stops));
                // While a ride is under way the first tab is that ride, and the route
                // waits in «Новый заказ» until it is over.
                navigate('/');
              }}
              onRemove={() => ask(ride)}
            />
          ))}
          {whole && (
            // At the foot of the list, where it is reached on purpose rather than
            // brushed past on the way to a card.
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <Soft className="bare" icon="delete_outline" onClick={() => ask('all')}>
                Очистить историю
              </Soft>
            </div>
          )}
        </div>
      )}

      {removing && (
        <ConfirmSheet
          title={removing === 'all' ? 'Очистить историю?' : 'Удалить поездку из истории?'}
          hint={
            removing === 'all'
              ? 'Все прошедшие и отменённые поездки пропадут из истории. Вернуть их будет нельзя.'
              : 'Поездка пропадёт из истории. Вернуть её будет нельзя.'
          }
          label={removing === 'all' ? 'Очистить историю' : 'Удалить'}
          busy={busy}
          error={error}
          onConfirm={() => void remove()}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/**
 * One ride that is over. Folded, it is the day and what the ride cost over its two
 * ends, each beside the time the driver marked there. Opened, the ends give way to
 * the whole route, labelled, and under it the order's number, the number to pay the
 * driver on where the ride was driven to its end or a driver was booked when it was
 * cancelled, and «Повторить» with «Удалить из истории» under it — but no times,
 * nothing else of the driver, and no word on why a ride was cancelled.
 */
function HistoryCard({
  ride,
  open,
  onToggle,
  onRepeat,
  onRemove,
}: {
  ride: PassengerRideView;
  open: boolean;
  onToggle: () => void;
  onRepeat: () => void;
  onRemove: () => void;
}) {
  const cancelled = isCancelled(ride);
  const boarded = markedAt(ride, 'STARTED');
  const arrived = markedAt(ride, 'FINISHED');
  // Null on a ride nobody was charged for, and on one whose driver has no number a
  // transfer can be made to. A cancelled ride is charged nothing, but one cancelled
  // with a driver booked still has them on it — the server takes off only a driver
  // who handed it back — and the passenger may want to pay them for the trip out
  // all the same.
  const transferPhone =
    ride.payment?.transferPhone ?? (cancelled ? ride.driver?.transferPhone : null);

  return (
    <div className="history-card">
      <button type="button" className="history-hit" onClick={onToggle} aria-expanded={open}>
        <span className="row" style={{ alignItems: 'baseline' }}>
          <span className="grow capitals">{dayLabel(new Date(ride.createdAt))}</span>
          {cancelled ? (
            <span className="capitals danger">Отменена</span>
          ) : (
            <span className="numeral" style={{ fontSize: 14 }}>
              {rubles(ride.payment?.totalKopeks ?? ride.fare.totalKopeks)}
            </span>
          )}
        </span>
        {!open && (
          <span style={{ display: 'block', marginTop: 14 }}>
            <RouteEnds
              from={pickupOf(ride.stops)}
              to={destinationOf(ride.stops)}
              // A ride that never boarded has no «Посадка» to show, so its pickup
              // carries the time it was ordered.
              fromTime={hhmm(boarded ?? new Date(ride.createdAt))}
              toTime={arrived ? hhmm(arrived) : null}
            />
          </span>
        )}
      </button>
      {open && (
        <div className="history-open">
          <div onClick={onToggle}>
            <RouteDetail stops={ride.stops} />
          </div>
          <div className="hairline" style={{ margin: '16px 0 14px' }} />
          {/* «ЗАКАЗ № 1042», as the driver's order cards write it: what поддержка
              will ask for. */}
          <div className="row order-number">
            <span className="label" style={{ color: 'var(--ink)' }}>
              Заказ №
            </span>
            <b>{ride.number}</b>
          </div>
          {/* «Перевод на номер телефона», as «К оплате» had it when the ride ended.
              A ride is not always paid for at the kerb — the passenger settles up
              from home, or somebody else does for a child who rode alone — and this
              is where the number is found afterwards (ADR 0004). */}
          {transferPhone && (
            <div className="history-transfer">
              <p className="to-pay-section">Перевод на номер телефона</p>
              <TransferNumber phone={transferPhone} />
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <Button tone="tint" size="tile" onClick={onRepeat}>
              Повторить
            </Button>
          </div>
          {/* Quiet and apart from «Повторить»: what the card is mostly opened for is
              the route again, not losing it. */}
          <div style={{ textAlign: 'center', marginTop: 6 }}>
            <Soft tone="stop" className="bare" icon="delete_outline" onClick={onRemove}>
              Удалить из истории
            </Soft>
          </div>
        </div>
      )}
    </div>
  );
}

/** When the driver marked [kind] on [ride], if they ever did. */
function markedAt(ride: PassengerRideView, kind: string): Date | null {
  const entry = ride.feed.find((one) => one.kind === kind);
  return entry ? new Date(entry.at) : null;
}

const dayKey = (when: Date): string =>
  `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, '0')}-${String(when.getDate()).padStart(2, '0')}`;

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

/**
 * The month grid. A day with no rides is not tappable — filtering to an empty list is
 * a dead end the passenger has to back out of.
 */
function Calendar({
  month,
  selected,
  days,
  onMonth,
  onPick,
}: {
  month: Date;
  selected: string | null;
  days: Set<string>;
  onMonth: (delta: number) => void;
  onPick: (day: string | null) => void;
}) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  // Monday is the first column, the way a Russian calendar is drawn.
  const leading = (first.getDay() + 6) % 7;

  return (
    <div className="calendar">
      <div className="row" style={{ gap: 0 }}>
        <IconButton icon="chevron_left" size={18} label="Назад" onClick={() => onMonth(-1)} />
        <span className="grow calendar-month">
          {MONTHS[month.getMonth()]} {month.getFullYear()}
        </span>
        <IconButton icon="chevron_right" size={18} label="Вперёд" onClick={() => onMonth(1)} />
      </div>

      {selected && (
        <div style={{ textAlign: 'right' }}>
          <button type="button" className="linkish" style={{ fontSize: 12, padding: '8px 10px' }} onClick={() => onPick(null)}>
            Все даты
          </button>
        </div>
      )}

      <div className="calendar-grid">
        {WEEKDAYS.map((label) => (
          <span key={label} className="label calendar-weekday">
            {label}
          </span>
        ))}
        {Array.from({ length: leading }, (_, i) => (
          <span key={`pad-${i}`} />
        ))}
        {Array.from({ length: count }, (_, i) => {
          const date = new Date(month.getFullYear(), month.getMonth(), i + 1);
          const key = dayKey(date);
          const has = days.has(key);
          return (
            <button
              key={key}
              type="button"
              className={`day${selected === key ? ' on' : ''}${has ? ' has' : ''}`}
              disabled={!has}
              onClick={() => onPick(selected === key ? null : key)}
            >
              {i + 1}
              <span className="dot" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
